/* eslint-disable no-console */
import {ExclamationCircleOutlined} from '@ant-design/icons';
import {Collapse, Form, Button, Tooltip} from 'antd';
import {useForm} from 'antd/es/form/Form';
import React, {Fragment, useEffect, useMemo, useState} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';

import Loader from '../../../components/Loader/Loader';
// import {Routes} from '../../../constants/Routes';
import {APITypeEnum} from '../../../services/interface/tableAPIType';
import esgAPI from '../../../services/modules/ESG/ESGAPI';
import pillarsAPI from '../../../services/modules/ESG/pillarsAPI';
import cityJSON from '../../../services/modules/JSON/cityJSON';
import country from '../../../services/modules/JSON/country';
import statesJSON from '../../../services/modules/JSON/statesJSON';
import {replaceCurrentAndPrevious} from '../../../shared/checkSelectedYear';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import useUserStore from '../../../store/userStore/userStore';
import {checkCurrentYearOrBelow} from '../../../utils/commonFunctions';
import showMessage from '../../../utils/showMessage';
import {cbamSection4IronAndSteelProcessLevelProductionAndEmissionsDataQuestions} from '../constants/cbam/IronandSteel/ironandSteelObj';
import {processIdentificationKeys} from '../constants/cbam/IronandSteel/ironandSteelQuestionsKeys';
import {isQuestionVisible} from '../constants/commonInputFormFunction';
import SubmitDisabledContext from '../constants/SubmitDisabledContext';
import {IField, inputType} from '../interface/inputType';

import AccordionCompletionHeader from './AccordionCompletionHeader';
import QuestionnaireSwitch from './QuestionnaireSwitch';

const Questionnaire: React.FC<{
  tabId: string;
  items: any[];
  accordion?: boolean;
  nextTabId: string;
  setDefaultKey: React.Dispatch<React.SetStateAction<string>>;
  APIRoute: string;
  setIsSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  principleTitle: string;
}> = ({
  tabId,
  items,
  accordion = true,
  nextTabId,
  setDefaultKey,
  APIRoute,
  setIsSubmitted,
  principleTitle
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {framework} = useParams();
  const selectedYear = checkCurrentYearOrBelow(
    searchParams.get('selectedYear')
  );
  const [isLoading, setIsLoading] = useState(true);
  const [onlySubmit, setOnlySubmit] = useState(true);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<any>();
  const [validationData, setValidationData] = useState<any>();
  const companyId = useCompanyStore.getState().companyId;
  const userDetail = useUserStore.getState().userDetails;
  // const navigate = useNavigate();

  const [form] = useForm();
  useEffect(() => {
    if (selectedYear) {
      searchParams.set('selectedYear', selectedYear);
      setSearchParams(searchParams);
      Promise.all([
        country(),
        statesJSON(),
        cityJSON(),
        esgAPI(
          APITypeEnum.GET_ALL,
          APIRoute,
          undefined,
          tabId,
          selectedYear ?? ''
        )
          .then((res) => {
            setFormData(res);
            setValidationData(res?.validationData ?? null);
          })
          .catch(() => {
            form.resetFields();
            setFormData({});
          })
          .finally(() => {
            setIsLoading(false);
          })
      ])
        .catch(() => {})
        .finally(() => {});
    }
  }, [tabId, selectedYear]);

  const processedItems = useMemo(() => {
    if (
      tabId !==
      cbamSection4IronAndSteelProcessLevelProductionAndEmissionsDataQuestions
        .tabs[0]?.id
    ) {
      return items;
    }

    if (!Array.isArray(items) || !validationData) {
      return [];
    }

    const PROCESS_SETUP_169_B_KEY =
      processIdentificationKeys.PROCESS_IDENTIFICATION_QUESTION_4_1_1;
    const validationEntryWithFixedColumn =
      validationData[PROCESS_SETUP_169_B_KEY];

    if (
      !validationEntryWithFixedColumn ||
      !Array.isArray(validationEntryWithFixedColumn.fixedColumn) ||
      validationEntryWithFixedColumn.fixedColumn.length === 0
    ) {
      // No fixedColumn for this key => hide Section 4 questions
      return [];
    }

    const fixedColumns = validationEntryWithFixedColumn.fixedColumn;

    const groupedItems: any[] = [];

    fixedColumns.forEach((col: any, index: number) => {
      const rowId = col?.rowId ?? col?.id ?? col?.value ?? `${index + 1}`;
      const suffix = `_${rowId}`;

      const title =
        col?.scopeLabel ??
        col?.label ??
        col?.name ??
        col?.title ??
        `Item ${index + 1}`;

      const sectionItems = items?.map((mainItem: any) => {
        return {
          ...mainItem,
          key: `${mainItem.key}${suffix}`,
          items: Array.isArray(mainItem.items)
            ? mainItem?.items?.map((questionItem: any) => {
                const newFields = Array.isArray(questionItem.field)
                  ? questionItem?.field?.map((field: IField) => {
                      const newField: IField = {
                        ...field,
                        fieldName: `${field.fieldName}${suffix}`
                      } satisfies IField;

                      if (
                        field.autoPopulate &&
                        field.fieldType !== inputType.CHECKBOXGROUP
                      ) {
                        newField.disabled = true;
                        newField.defaultValue = title;
                      }

                      if (field.requiredOn?.fieldName) {
                        newField.requiredOn = {
                          ...field.requiredOn,
                          fieldName: `${field.requiredOn.fieldName}${suffix}`
                        };
                      }

                      if (field.conditionalVisibility?.dependsOn) {
                        newField.conditionalVisibility = {
                          ...field.conditionalVisibility,
                          dependsOn: `${field.conditionalVisibility.dependsOn}${suffix}`
                        };
                      }

                      if (field.disabledOn?.fieldName) {
                        newField.disabledOn = {
                          ...field.disabledOn,
                          fieldName: `${field.disabledOn.fieldName}${suffix}`
                        };
                      }

                      return newField;
                    })
                  : questionItem.field;

                return {
                  ...questionItem,
                  conditionalVisibility: questionItem?.conditionalVisibility
                    ? {
                        ...questionItem.conditionalVisibility,
                        dependsOn: questionItem?.conditionalVisibility
                          ?.dependsOn
                          ? `${questionItem.conditionalVisibility.dependsOn}${suffix}`
                          : questionItem.conditionalVisibility.dependsOn
                      }
                    : questionItem?.conditionalVisibility,
                  field: newFields
                };
              })
            : mainItem.items
        };
      });

      groupedItems.push({
        key: `process_${rowId}`,
        label: title,
        sections: sectionItems
      });
    });

    return groupedItems;
  }, [items, tabId, validationData]);

  const handleSubmit = (values: any) => {
    const updatedValues = updateUndefinedFieldsToNull(values);

    const data = {
      type: tabId,
      data: {
        ...updatedValues
      },
      year: selectedYear,
      company: companyId,
      createdBy: userDetail?._id,
      updatedBy: userDetail?._id
    };
    setIsSubmitting(true);
    esgAPI(
      formData?._id ? APITypeEnum.PUT : APITypeEnum.POST_NEW,
      APIRoute,
      data,
      '',
      '',
      formData?._id ?? ''
    )
      .then((res) => {
        pillarsAPI(selectedYear ?? '', true, framework ?? undefined)
          .then(() => {})
          .catch(() => {})
          .finally(() => {
            showMessage(res?.data);
            setIsSubmitted(true);
            setIsSubmitting(false);
            setIsSubmitDisabled(true);
            if (!onlySubmit) {
              handleSaveAndNext();
            }
          });
        setFormData(res.data.data);
      })
      .catch(() => {})
      .finally(() => {});
  };

  const updateUndefinedFieldsToNull = (newObj: any) => {
    for (const key in newObj) {
      if (newObj.hasOwnProperty(key) && newObj[key] === undefined) {
        newObj[key] = null;
      }
    }

    return newObj;
  };

  const handleSaveAndNext = () => {
    setDefaultKey(nextTabId);
    window.scrollTo(0, 0);
  };

  const tooltipCSS = {
    width: 'auto',
    maxWidth: '500px',
    height: 'auto',
    padding: '12px'
  };

  const shouldShowSection4Message =
    tabId ===
      cbamSection4IronAndSteelProcessLevelProductionAndEmissionsDataQuestions
        .tabs[0]?.id &&
    Array.isArray(items) &&
    processedItems.length === 0;

  const handleAccordionChange = () => {
    window.requestAnimationFrame(() => {
      const accordionContainer = document.querySelector(
        '.moduleForm .ant-collapse'
      );

      if (accordionContainer instanceof HTMLElement) {
        const top =
          accordionContainer.getBoundingClientRect().top + window.scrollY - 16;
        window.scrollTo({top, behavior: 'smooth'});

        return;
      }

      window.scrollTo({top: 0, behavior: 'smooth'});
    });
  };

  return isLoading ? (
    <Loader />
  ) : (
    <>
      {principleTitle && (
        <div
          style={{
            font: 'normal normal 600 18px/17px Montserrat',
            color: '#000000',
            margin: '2.5rem 0'
          }}
        >
          {principleTitle}
        </div>
      )}
      <Form
        layout="horizontal"
        onFinish={handleSubmit}
        initialValues={formData?.data}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;

          const target = e.target as unknown;
          if (
            target instanceof HTMLInputElement &&
            (target.type === 'checkbox' || target.type === 'radio')
          ) {
            e.preventDefault();
            target.click();
          }
        }}
        onValuesChange={() => {
          setIsSubmitDisabled(false);
        }}
        // disabled={
        //   hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
        // }
        id="moduleForm"
        form={form}
        className="moduleForm"
      >
        {shouldShowSection4Message && (
          <div
            style={{
              marginBottom: 16,
              padding: '12px 16px',
              borderRadius: 4,
              background: '#fff1f0',
              border: '1px solid #ffa39e',
              color: '#cf1322'
            }}
          >
            Please fill Section 1 and then return to this section.
          </div>
        )}
        {accordion &&
          (tabId ===
          cbamSection4IronAndSteelProcessLevelProductionAndEmissionsDataQuestions
            .tabs[0]?.id ? (
            // Outer accordion per fixed row; inner content is full Section 4
            <Collapse
              defaultActiveKey={[processedItems?.[0]?.key ?? '1']}
              accordion
              destroyInactivePanel={false}
              onChange={handleAccordionChange}
            >
              {processedItems.map((group: any) => {
                const replaceLabel = replaceCurrentAndPrevious(
                  group?.label,
                  selectedYear ?? ''
                );
                const allGroupQuestions = Array.isArray(group?.sections)
                  ? group.sections.flatMap((section: any) =>
                      Array.isArray(section?.items) ? section.items : []
                    )
                  : [];

                return (
                  <Collapse.Panel
                    forceRender={true}
                    header={
                      <AccordionCompletionHeader
                        label={replaceLabel}
                        questions={allGroupQuestions}
                        validationData={validationData}
                      />
                    }
                    key={group.key}
                  >
                    <Collapse
                      defaultActiveKey={[group.sections?.[0]?.key ?? '1']}
                      accordion
                      destroyInactivePanel={false}
                      onChange={handleAccordionChange}
                    >
                      {group.sections.map((section: any) => {
                        const sectionLabel = replaceCurrentAndPrevious(
                          section?.label,
                          selectedYear ?? ''
                        );

                        return (
                          <Collapse.Panel
                            forceRender={true}
                            header={
                              <AccordionCompletionHeader
                                label={sectionLabel}
                                kpiId={section?.kpiId}
                                questions={section?.items ?? []}
                                validationData={validationData}
                              />
                            }
                            key={section.key}
                          >
                            {section.items.map((item: any, index: number) => {
                              return (
                                <Form.Item
                                  key={index}
                                  noStyle
                                  shouldUpdate
                                  dependencies={
                                    item?.conditionalVisibility?.dependsOn
                                      ? [item.conditionalVisibility.dependsOn]
                                      : []
                                  }
                                >
                                  {({getFieldValue}) => {
                                    const shouldShowQuestion =
                                      isQuestionVisible(item, getFieldValue);

                                    // Check validationData - hide question if all fields are inactive
                                    if (
                                      validationData &&
                                      item?.field &&
                                      Array.isArray(item.field) &&
                                      item.field.length > 0 &&
                                      item.field.every(
                                        (field: IField) =>
                                          field?.fieldName &&
                                          validationData[field.fieldName]
                                            ?.active === false
                                      )
                                    ) {
                                      return null;
                                    }

                                    if (!shouldShowQuestion) return null;

                                    return (
                                      <div style={{padding: 5}}>
                                        <div>
                                          {item?.headings?.map(
                                            (head: any, headIndex: number) => {
                                              const replaceHeading =
                                                replaceCurrentAndPrevious(
                                                  head?.heading,
                                                  selectedYear ?? ''
                                                );
                                              switch (head.headingType) {
                                                case inputType.HEADING_5:
                                                  return (
                                                    <h5 key={headIndex}>
                                                      {replaceHeading}
                                                    </h5>
                                                  );
                                                case inputType.HEADING_4:
                                                  return (
                                                    <h4 key={headIndex}>
                                                      {replaceHeading}
                                                    </h4>
                                                  );
                                                case inputType.HEADING_3:
                                                  return (
                                                    <h3 key={headIndex}>
                                                      {replaceHeading}
                                                    </h3>
                                                  );
                                                case inputType.HEADING_2:
                                                  return (
                                                    <h2 key={headIndex}>
                                                      {replaceHeading}
                                                    </h2>
                                                  );
                                                case inputType.HEADING_1:
                                                  return (
                                                    <h1 key={headIndex}>
                                                      {replaceHeading}
                                                    </h1>
                                                  );
                                                default:
                                                  return (
                                                    <h2 key={headIndex}>
                                                      {replaceHeading}
                                                    </h2>
                                                  );
                                              }
                                            }
                                          )}
                                        </div>
                                        <div>
                                          {item.question === '' ? (
                                            <></>
                                          ) : item?.tooltip ? (
                                            <>
                                              <b>{item.question}</b>
                                              <Tooltip
                                                title={item.tooltip}
                                                autoAdjustOverflow={true}
                                                arrow={false}
                                                overlayStyle={tooltipCSS}
                                              >
                                                <ExclamationCircleOutlined className="iButtonIcon" />
                                              </Tooltip>
                                            </>
                                          ) : (
                                            <b>{item.question}</b>
                                          )}
                                        </div>
                                        <div style={{marginTop: 7}}>
                                          <SubmitDisabledContext.Provider
                                            value={setIsSubmitDisabled}
                                          >
                                            {item.field.map(
                                              (
                                                field: IField,
                                                fieldIndex: number
                                              ) => {
                                                return (
                                                  <QuestionnaireSwitch
                                                    field={{
                                                      ...field,
                                                      fieldLabel:
                                                        replaceCurrentAndPrevious(
                                                          field.fieldLabel,
                                                          selectedYear ?? ''
                                                        ),
                                                      validationData
                                                    }}
                                                    key={fieldIndex}
                                                  />
                                                );
                                              }
                                            )}
                                          </SubmitDisabledContext.Provider>
                                        </div>
                                      </div>
                                    );
                                  }}
                                </Form.Item>
                              );
                            })}
                          </Collapse.Panel>
                        );
                      })}
                    </Collapse>
                  </Collapse.Panel>
                );
              })}
            </Collapse>
          ) : (
            <Collapse
              defaultActiveKey={[processedItems?.[0]?.key ?? '1']}
              accordion
              destroyInactivePanel={false}
              onChange={handleAccordionChange}
            >
              {processedItems.map((item) => {
                const replaceLabel = replaceCurrentAndPrevious(
                  item?.label,
                  selectedYear ?? ''
                );

                return (
                  <Collapse.Panel
                    forceRender={true}
                    header={
                      <AccordionCompletionHeader
                        label={replaceLabel}
                        kpiId={item?.kpiId}
                        questions={item?.items ?? []}
                        validationData={validationData}
                      />
                    }
                    key={item.key}
                  >
                    {item.items.map((item: any, index: number) => {
                      return (
                        <Form.Item
                          key={index}
                          noStyle
                          shouldUpdate
                          dependencies={
                            item?.conditionalVisibility?.dependsOn
                              ? [item.conditionalVisibility.dependsOn]
                              : []
                          }
                        >
                          {({getFieldValue}) => {
                            const shouldShowQuestion = isQuestionVisible(
                              item,
                              getFieldValue
                            );

                            // Check validationData - hide question if all fields are inactive
                            if (
                              validationData &&
                              item?.field &&
                              Array.isArray(item.field) &&
                              item.field.length > 0 &&
                              item.field.every(
                                (field: IField) =>
                                  field?.fieldName &&
                                  validationData[field.fieldName]?.active ===
                                    false
                              )
                            ) {
                              return null;
                            }

                            if (!shouldShowQuestion) return null;

                            return (
                              <div style={{padding: 5}}>
                                <div>
                                  {item?.headings?.map(
                                    (head: any, index: number) => {
                                      const replaceHeading =
                                        replaceCurrentAndPrevious(
                                          head?.heading,
                                          selectedYear ?? ''
                                        );
                                      switch (head.headingType) {
                                        case inputType.HEADING_5:
                                          return (
                                            <h5 key={index}>
                                              {replaceHeading}
                                            </h5>
                                          );
                                        case inputType.HEADING_4:
                                          return (
                                            <h4 key={index}>
                                              {replaceHeading}
                                            </h4>
                                          );
                                        case inputType.HEADING_3:
                                          return (
                                            <h3 key={index}>
                                              {replaceHeading}
                                            </h3>
                                          );
                                        case inputType.HEADING_2:
                                          return (
                                            <h2 key={index}>
                                              {replaceHeading}
                                            </h2>
                                          );
                                        case inputType.HEADING_1:
                                          return (
                                            <h1 key={index}>
                                              {replaceHeading}
                                            </h1>
                                          );
                                        default:
                                          return (
                                            <h2 key={index}>
                                              {replaceHeading}
                                            </h2>
                                          );
                                      }
                                    }
                                  )}
                                </div>
                                <div>
                                  {item.question === '' ? (
                                    <></>
                                  ) : item?.tooltip ? (
                                    <>
                                      <b>{item.question}</b>
                                      <Tooltip
                                        title={item.tooltip}
                                        autoAdjustOverflow={true}
                                        arrow={false}
                                        overlayStyle={tooltipCSS}
                                      >
                                        <ExclamationCircleOutlined className="iButtonIcon" />
                                      </Tooltip>
                                    </>
                                  ) : (
                                    <b>{item.question}</b>
                                  )}
                                </div>
                                <div style={{marginTop: 7}}>
                                  <SubmitDisabledContext.Provider
                                    value={setIsSubmitDisabled}
                                  >
                                    {item.field.map(
                                      (field: IField, fieldIndex: number) => {
                                        // eslint-disable-next-line react/jsx-key
                                        return (
                                          <QuestionnaireSwitch
                                            field={{
                                              ...field,
                                              fieldLabel:
                                                replaceCurrentAndPrevious(
                                                  field.fieldLabel,
                                                  selectedYear ?? ''
                                                ),
                                              validationData
                                            }}
                                            key={fieldIndex}
                                          />
                                        );
                                      }
                                    )}
                                  </SubmitDisabledContext.Provider>
                                </div>
                              </div>
                            );
                          }}
                        </Form.Item>
                      );
                    })}
                  </Collapse.Panel>
                );
              })}
            </Collapse>
          ))}
        {!accordion && (
          <>
            {processedItems.map((mainItem, id) => {
              return (
                <Fragment key={id}>
                  {mainItem.items.map((item: any, index: number) => {
                    return (
                      <Form.Item
                        key={index}
                        noStyle
                        shouldUpdate
                        dependencies={
                          item?.conditionalVisibility?.dependsOn
                            ? [item.conditionalVisibility.dependsOn]
                            : []
                        }
                      >
                        {({getFieldValue}) => {
                          const shouldShowQuestion = isQuestionVisible(
                            item,
                            getFieldValue
                          );

                          // Check validationData - hide question if all fields are inactive
                          if (
                            validationData &&
                            item?.field &&
                            Array.isArray(item.field) &&
                            item.field.length > 0 &&
                            item.field.every(
                              (field: IField) =>
                                field?.fieldName &&
                                validationData[field.fieldName]?.active ===
                                  false
                            )
                          ) {
                            return null;
                          }

                          if (!shouldShowQuestion) return null;

                          return (
                            <div style={{padding: 5}}>
                              <div>
                                {item?.headings?.map(
                                  (head: any, index: number) => {
                                    const replaceHeading =
                                      replaceCurrentAndPrevious(
                                        head?.heading,
                                        selectedYear ?? ''
                                      );
                                    switch (head.headingType) {
                                      case inputType.HEADING_5:
                                        return (
                                          <h5 key={index}>{replaceHeading}</h5>
                                        );
                                      case inputType.HEADING_4:
                                        return (
                                          <h4 key={index}>{replaceHeading}</h4>
                                        );
                                      case inputType.HEADING_3:
                                        return (
                                          <h3 key={index}>{replaceHeading}</h3>
                                        );
                                      case inputType.HEADING_2:
                                        return (
                                          <h2 key={index}>{replaceHeading}</h2>
                                        );
                                      case inputType.HEADING_1:
                                        return (
                                          <h1 key={index}>{replaceHeading}</h1>
                                        );
                                      default:
                                        return (
                                          <h2 key={index}>{replaceHeading}</h2>
                                        );
                                    }
                                  }
                                )}
                              </div>
                              <div>
                                {item.question === '' ? (
                                  <></>
                                ) : item?.tooltip ? (
                                  <>
                                    <b>{item.question}</b>
                                    <Tooltip
                                      title={item.tooltip}
                                      autoAdjustOverflow={true}
                                      arrow={false}
                                      overlayStyle={tooltipCSS}
                                    >
                                      <ExclamationCircleOutlined className="iButtonIcon" />
                                    </Tooltip>
                                  </>
                                ) : (
                                  <b>
                                    {`${
                                      mainItem?.showIndex === false
                                        ? ''
                                        : `${index + 1} .`
                                    } ${item.question ?? ''}`}
                                  </b>
                                )}
                              </div>
                              <div style={{marginTop: 5}}>
                                <SubmitDisabledContext.Provider
                                  value={setIsSubmitDisabled}
                                >
                                  {item?.field?.map(
                                    (
                                      fieldParam: IField,
                                      fieldIndex: number
                                    ) => {
                                      return (
                                        <QuestionnaireSwitch
                                          field={{
                                            ...fieldParam,
                                            fieldLabel:
                                              replaceCurrentAndPrevious(
                                                fieldParam.fieldLabel,
                                                selectedYear ?? ''
                                              ),
                                            validationData
                                          }}
                                          key={fieldIndex}
                                        />
                                      );
                                    }
                                  )}
                                </SubmitDisabledContext.Provider>
                              </div>
                            </div>
                          );
                        }}
                      </Form.Item>
                    );
                  })}
                </Fragment>
              );
            })}
          </>
        )}
        <div style={{float: 'right', marginTop: 20}}>
          <Button
            type="primary"
            htmlType="submit"
            disabled={
              isSubmitDisabled ? true : isSubmitting ? !onlySubmit : false
            }
            onClick={() => {
              setOnlySubmit(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                form.submit();
                e.preventDefault();
                setOnlySubmit(true);
              }
            }}
            loading={isSubmitting ? onlySubmit : false}
          >
            Submit
          </Button>
          {nextTabId ? (
            <Button
              type="primary"
              htmlType="submit"
              style={{marginLeft: 5}}
              disabled={
                isSubmitDisabled ? true : isSubmitting ? onlySubmit : false
              }
              name="submitNext"
              onClick={() => {
                setOnlySubmit(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  form.submit();
                  e.preventDefault();
                  setOnlySubmit(false);
                }
              }}
              loading={isSubmitting ? !onlySubmit : false}
            >
              Save & next
            </Button>
          ) : (
            ''
          )}
        </div>
      </Form>
    </>
  );
};

export default Questionnaire;
